import React, { useCallback, useRef } from 'react';
import type { Asset } from '#/graphql/types';
import { useNavigate } from 'react-router-dom';
import List from '#/common/components/List';
import { gql } from '@apollo/client';
import { getAssetList } from '#/graphql/queries';
import conditionStatusMap from '../../utils/conditionStatusMap';
import columns from '../AssetDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import useAssetDelete from '../../hooks/useDeleteAssetMutation';
import useToast from '#/common/hooks/useToast';
import { List as DxList } from 'devextreme-react/list';

const getAssetListQuery = gql(getAssetList);

const getStatus = (asset: Asset) => conditionStatusMap?.[asset.condition ?? ''];

export interface AssetListProps {
  permanentFilters?: Filter[];
}

const AssetList: React.FC<AssetListProps> = (props) => {
  const { permanentFilters } = props;

  const list = useRef<DxList | null>(null);
  const { confirm } = useToast();
  const { deleteAsset } = useAssetDelete();
  const navigate = useNavigate();

  const openAsset = useCallback(
    (asset: Asset) => {
      navigate(`/assets/${asset.id}`);
    },
    [navigate],
  );

  const deleteItem = useCallback(
    async (items?: Asset[], clearSelectedItems?: () => void) => {
      if (!items) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these assets?`);
      if (!sure) {
        return;
      }
      await deleteAsset(items.map((item) => item.id).join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteAsset],
  );

  return (
    <List<Asset>
      setRef={(ref) => (list.current = ref)}
      getItemsQuery={getAssetListQuery}
      permanentFilters={permanentFilters}
      titleKey="model.name"
      status={getStatus}
      onClick={openAsset}
      subtitleKey="assetTag"
      bodyFields={[
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'assetType.name', label: 'Asset Type' },
        { key: 'manufacturer.name', label: 'Manufacturer' },
        { key: 'model.name', label: 'Model' },
      ]}
      columnList={columns}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(AssetList);
